import { useEffect, useRef } from 'react';
import localforage from 'localforage';

/**
 * Persists form state to IndexedDB per config instance.
 * Each property type gets its own isolated store (no cross-form data bleed).
 *
 * @param {object} config    - PROPERTY_CONFIGS entry
 * @param {object} upload    - current form state
 * @param {Function} setUpload
 * @param {Function} [onRestored] - called with saved data when form is restored on mount
 * @returns {{ clearStorage: Function }}
 */
export function useFormPersistence(config, upload, setUpload, onRestored) {
  // createInstance isolates each form type in its own DB — avoids the global
  // localforage.config() mutation the old forms used, which caused data bleed
  // between OtherForm and CondoForm.
  const store = useRef(
    localforage.createInstance({
      name: config.dbName,
      storeName: config.storeName,
    })
  );

  // Persist on every upload change
  useEffect(() => {
    store.current.setItem(config.storageKey, upload).catch(() => {});
  }, [upload, config.storageKey]);

  // Restore on mount
  useEffect(() => {
    store.current
      .getItem(config.storageKey)
      .then((saved) => {
        if (saved) {
          setUpload(saved);
          onRestored?.(saved);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearStorage = () =>
    store.current.dropInstance({ name: config.dbName }).catch(() => {});

  return { clearStorage };
}
